'use client';

import type { Metadata } from 'next';
import Image from 'next/image';
import { useState } from 'react';
import LazyLiquidEther from '@/components/backgrounds/LazyLiquidEther';

/**
 * Amenities Page
 * ==============
 *
 * Showcases the premium amenities at Magnate Regalia with actual images.
 * Features:
 * - Hero section with overview
 * - Featured amenities with large images
 * - Grid of all amenities
 * - Image modal for full-screen viewing
 */

// Featured amenities with images
const featuredAmenities = [
  {
    id: 'gym',
    name: 'Fully Equipped Gymnasium',
    description: 'State-of-the-art fitness center with modern equipment for cardio, strength training, and flexibility exercises. Stay fit without leaving your home.',
    image: '/assets/images/GYM_JPEG.webp',
    features: ['Modern Equipment', 'Cardio Zone', 'Strength Training', 'Personal Training Area'],
  },
  {
    id: 'theater',
    name: 'Private Theater',
    description: 'Experience cinema-quality entertainment in our exclusive private theater. Perfect for movie nights, sports events, and community gatherings.',
    image: '/assets/images/Theater_JPEG.webp',
    features: ['4K Projection', 'Dolby Sound', 'Plush Seating', 'Private Screenings'],
  },
  {
    id: 'cafe',
    name: 'Cafe & Lounge',
    description: 'A sophisticated space to unwind, socialize, or work remotely. Enjoy premium beverages and light snacks in an elegant ambiance.',
    image: '/assets/images/CAFE_JPEG.webp',
    features: ['Coffee Bar', 'Comfortable Seating', 'Wi-Fi Enabled', 'Meeting Space'],
  },
  {
    id: 'banquet',
    name: 'Grand Banquet Hall',
    description: 'Host memorable celebrations in our elegantly designed banquet hall. Perfect for weddings, parties, and corporate events.',
    image: '/assets/images/Banquet_Final_JPEG.webp',
    features: ['Elegant Decor', 'Catering Kitchen', 'Audio-Visual', '200+ Capacity'],
  },
  {
    id: 'sauna',
    name: 'Sauna & Wellness',
    description: 'Rejuvenate your body and mind in our premium sauna facility. Experience deep relaxation and improved well-being.',
    image: '/assets/images/Sauna_JPEG.webp',
    features: ['Finnish Sauna', 'Temperature Control', 'Private Sessions', 'Health Benefits'],
  },
  {
    id: 'steam',
    name: 'Steam Room',
    description: 'Indulge in therapeutic steam sessions that promote relaxation, improve circulation, and detoxify your body naturally.',
    image: '/assets/images/steam_JPEG.webp',
    features: ['Aromatherapy', 'Detoxification', 'Skin Benefits', 'Stress Relief'],
  },
  {
    id: 'indoor-games',
    name: 'Indoor Games Zone',
    description: 'A dedicated space for recreation featuring table tennis, billiards, foosball, and other engaging indoor activities.',
    image: '/assets/images/INDOOR GAMES_JPEG.webp',
    features: ['Table Tennis', 'Billiards', 'Foosball', 'Board Games'],
  },
  {
    id: 'toddler',
    name: 'Toddler Play Area',
    description: 'A safe and stimulating environment designed specially for young children with age-appropriate play equipment and soft flooring.',
    image: '/assets/images/Toddler_JPEG.webp',
    features: ['Safe Equipment', 'Soft Flooring', 'Supervised Area', 'Educational Toys'],
  },
  {
    id: 'swimming-pool',
    name: 'Swimming Pool',
    description: 'A temperature-controlled swimming pool with a separate kids section. Perfect for morning laps, evening relaxation, or family fun time.',
    image: '/assets/images/Cam_24.webp',
    features: ['Temperature Controlled', 'Kids Section', 'Poolside Seating', 'Changing Rooms'],
  },
  {
    id: 'landscaped-gardens',
    name: 'Landscaped Gardens',
    description: 'Beautiful green spaces designed for relaxation and leisurely walks. Enjoy manicured lawns, flowering plants, and serene pathways throughout the property.',
    image: '/assets/images/Cam_19.webp',
    features: ['Manicured Lawns', 'Walking Paths', 'Seating Areas', 'Native Plants'],
  },
  {
    id: 'outdoor-sitting',
    name: 'Outdoor Sitting Area',
    description: 'Comfortable outdoor seating spaces perfect for casual conversations, reading, or simply enjoying the fresh air and beautiful surroundings.',
    image: '/assets/images/Cam_11.webp',
    features: ['Comfortable Seating', 'Shaded Areas', 'Garden Views', 'Social Spaces'],
  },
];

// Additional amenities without images
const additionalAmenities = [
  { name: 'Jogging Track', icon: '🏃', description: 'Dedicated track for morning jogs and evening walks' },
  { name: 'Yoga Deck', icon: '🧘', description: 'Open-air space for yoga and meditation' },
  { name: 'EV Charging', icon: '🔋', description: 'Electric vehicle charging stations in parking' },
  { name: '24/7 Security', icon: '🔒', description: 'Round-the-clock CCTV and security personnel' },
  { name: 'Senior Citizen Area', icon: '🌸', description: 'Peaceful seating area for elderly residents' },
  { name: 'Sky Garden', icon: '🌿', description: 'Elevated garden spaces on multiple floors' },
  { name: 'Party Lawn', icon: '🎉', description: 'Open lawn for outdoor celebrations' },
  { name: 'Power Backup', icon: '⚡', description: '100% power backup for uninterrupted living' },
  { name: 'High-Speed Elevators', icon: '🛗', description: 'Multiple high-speed elevators per tower' },
  { name: 'Fire Safety', icon: '🧯', description: 'Advanced fire detection and suppression systems' },
];

export default function AmenitiesPage() {
  const [selectedAmenity, setSelectedAmenity] = useState<typeof featuredAmenities[0] | null>(null);

  return (
    <main className="min-h-screen relative overflow-hidden">
      {/* Liquid Ether Background */}
      <div className="fixed inset-0 z-0">
        <LazyLiquidEther
          colors={['#D4A574', '#8B6914', '#C9A227']}
          mouseForce={20}
          cursorSize={100}
          resolution={0.5}
          isViscous={false}
          viscous={30}
          autoDemo={true}
          autoSpeed={0.5}
          autoIntensity={2.2}
          autoResumeDelay={2000}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Content wrapper - above background */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="pt-[108px] md:pt-32 pb-16 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-amber-400 font-medium tracking-wider mb-4">WORLD-CLASS FACILITIES</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Premium Amenities
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Experience world-class facilities designed for your comfort, wellness, and luxury lifestyle.
            Every amenity at Magnate Regalia is crafted to exceed your expectations.
          </p>
        </div>
      </section>

      {/* Foyer/Entrance Image */}
      <section className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="relative aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden">
            <Image
              src="/assets/images/Final_Foyer_JPEG.webp"
              alt="Grand Foyer Entrance"
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 1200px"
              preload
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <h2 className="text-3xl font-bold text-white mb-2">Grand Entrance Foyer</h2>
              <p className="text-white/70">Make an impression from the moment you arrive</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Amenities Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Featured Amenities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredAmenities.map((amenity) => (
              <div
                key={amenity.id}
                className="group bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden hover:border-amber-400/30 transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedAmenity(amenity)}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <Image
                    src={amenity.image}
                    alt={amenity.name}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white mb-1">{amenity.name}</h3>
                    <p className="text-white/70 text-sm line-clamp-2">{amenity.description}</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {amenity.features.map((feature) => (
                      <span
                        key={feature}
                        className="px-3 py-1 bg-white/5 border border-white/10 text-white/85 text-xs rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Amenities */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            More Amenities
          </h2>
          <p className="text-white/60 text-center mb-12 max-w-2xl mx-auto">
            Beyond our featured facilities, enjoy a comprehensive range of amenities
            designed for every aspect of modern living.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {additionalAmenities.map((amenity) => (
              <div
                key={amenity.name}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 hover:bg-white/10 transition-colors"
              >
                <span className="text-3xl mb-3 block">{amenity.icon}</span>
                <h3 className="text-base font-semibold text-white mb-1">{amenity.name}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{amenity.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Experience These Amenities
            </h2>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Schedule a site visit to experience our world-class amenities firsthand.
              See for yourself why Magnate Regalia sets the standard for luxury living.
            </p>
            <a
              href="/contact?message=I%20would%20like%20to%20schedule%20a%20site%20visit%20to%20Magnate%20Regalia.%20Please%20contact%20me%20to%20arrange%20a%20convenient%20time."
              className="inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              Schedule a Visit
              <span>→</span>
            </a>
          </div>
        </section>
      </div>

      {/* Image Modal */}
      {selectedAmenity && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedAmenity(null)}
        >
          <div
            className="relative max-w-5xl w-full bg-slate-900 rounded-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedAmenity(null)}
              className="absolute top-4 right-4 z-10 w-12 h-12 min-w-12 min-h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              aria-label="Close modal"
            >
              ✕
            </button>
            <div className="relative aspect-[16/9]">
              <Image
                src={selectedAmenity.image}
                alt={selectedAmenity.name}
                fill
                className="object-cover"
              />
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-white mb-2">{selectedAmenity.name}</h3>
              <p className="text-white/70 mb-4">{selectedAmenity.description}</p>
              <div className="flex flex-wrap gap-2">
                {selectedAmenity.features.map((feature) => (
                  <span
                    key={feature}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white/85 text-sm rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
